import React, { useState, useEffect } from 'react';
import LoadingAnimation from './LoadingAnimation';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Shield, HelpCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AcaciaTree from './AcaciaTree';
import GamificationCelebration from './GamificationCelebration';
import PlantHealthNotification from './PlantHealthNotification';
import GamificationTutorial from './GamificationTutorial';
import BadgeSystem from './BadgeSystem';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ISLAND_NAMES = {
  1: "Ilha 1",
  2: "Ilha 2",
  3: "Ilha 3",
  4: "Ilha 4",
  5: "Ilha 5",
};

const Profile = () => {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [pots, setPots] = useState([]);
  const [potsLoading, setPotsLoading] = useState(true);
  
  // Gamification enhancements
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationStage, setCelebrationStage] = useState(0);
  const [showTutorial, setShowTutorial] = useState(false);
  const [previousStage, setPreviousStage] = useState(0);

  useEffect(() => {
    if (authUser) {
      setUser(authUser);
      
      // Check if should show tutorial (first time visiting profile)
      const hasSeenTutorial = localStorage.getItem('simby_tutorial_seen');
      if (!hasSeenTutorial) {
        setShowTutorial(true);
      }
      
      // Store previous stage to detect level ups
      if (authUser.stage !== undefined) {
        const prevStage = parseInt(localStorage.getItem('simby_prev_stage') || '0');
        if (authUser.stage > prevStage && prevStage > 0) {
          // Level up! Show celebration
          setCelebrationStage(authUser.stage);
          setShowCelebration(true);
        }
        setPreviousStage(prevStage);
        localStorage.setItem('simby_prev_stage', authUser.stage.toString());
      }
    }
  }, [authUser]);

  useEffect(() => {
    if (!authUser) return;
    (async () => {
      try {
        const potsRes = await axios.get(`${API}/gamification/my-pots`, { withCredentials: true });
        setPots(potsRes.data || []);
      } catch {
        // ignore
      } finally {
        setPotsLoading(false);
      }
    })();
  }, [authUser]);

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <div className="text-center">
          <LoadingAnimation size="md" className="mx-auto mb-4" />
          <p className="text-gray-600">Carregando perfil...</p>
        </div>
      </div>
    );
  }

  const memberSince = user.createdAt
    ? new Date(user.createdAt).toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* Profile Header */}
        <Card className="p-8 bg-gradient-to-r from-purple-600 to-violet-700 text-white border-0">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <img
              src={user.avatar || user.picture || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}`}
              alt={user.name}
              className="w-24 h-24 rounded-full border-4 border-white shadow-lg flex-shrink-0"
            />
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-2xl font-bold mb-1">{user.name}</h1>
              <p className="text-purple-200 text-sm">{user.email}</p>
              {memberSince && (
                <p className="text-purple-300 text-xs mt-1">Membro desde {memberSince}</p>
              )}
            </div>
          </div>
        </Card>

        {/* Pot Gallery */}
        <Card className="p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">🌿 Minhas Acácias</h2>
          {potsLoading ? (
            <div className="flex gap-4">
              {[1, 2].map((i) => (
                <div key={i} className="w-24 h-28 rounded-xl bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : pots.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <div className="text-4xl mb-2">🌱</div>
              <p className="text-sm">Complete módulos para fazer sua acácia crescer!</p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-5">
              {pots.map((pot) => (
                <PotCard key={pot.pot_id} pot={pot} />
              ))}
            </div>
          )}
        </Card>

        {/* Learning Stats */}
        <Card className="p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">📊 Progresso</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <StatRow
              label="Módulos concluídos"
              value={(user.completedLessons || []).length}
              sub="ao longo da jornada"
            />
            <StatRow
              label="Ilhas iniciadas"
              value={pots.length}
              sub={`de 5 disponíveis`}
            />
            {pots.length > 0 && (
              <StatRow
                label="Pontos acumulados"
                value={pots.reduce((s, p) => s + (p.island_points || 0), 0)}
                sub="em todas as ilhas"
              />
            )}
            {user.lastPracticeAt && (
              <StatRow
                label="Última prática"
                value={new Date(user.lastPracticeAt).toLocaleDateString("pt-BR")}
                sub=""
              />
            )}
          </div>
        </Card>

        {/* LGPD */}
        <Card className="p-6 border-2 border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-bold text-gray-900">Privacidade e Dados</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Conforme a LGPD (Lei 13.709/2018), você pode visualizar, baixar ou excluir todos os seus dados pessoais.
          </p>
          <Button
            className="bg-purple-600 hover:bg-purple-700 text-white"
            onClick={() => navigate('/my-data')}
          >
            <Shield className="w-4 h-4 mr-2" />
            Gerenciar meus dados
          </Button>
        </Card>

        {/* Mascot */}
        <Card className="p-6 bg-gradient-to-r from-purple-100 to-purple-50 border-2 border-purple-200">
          <div className="flex items-center gap-4">
            <img src="/images/simby-mascote.png" alt="Simby" className="w-20 h-28 object-contain flex-shrink-0" />
            <div>
              <h3 className="font-bold text-gray-900 mb-1">Você está cultivando algo incrível!</h3>
              <p className="text-gray-600 text-sm">
                Continue regando sua acácia e compartilhando ecos com seu círculo 🌿
              </p>
            </div>
          </div>
        </Card>
        
        {/* Badge System */}
        <BadgeSystem
          userProgress={{
            completed_islands: user?.completed_islands_count || 0,
            stage: user?.stage || 0,
            streak: user?.streak || 0,
            completed_lessons: user?.completed_lessons_count || 0,
            times_watered: user?.times_watered || 0,
            circle_participation: user?.circle_echos_count || 0,
          }}
          onBadgeClick={(badge) => console.log('Badge clicked:', badge)}
        />
        
        {/* Tutorial Button */}
        <Card className="p-4 bg-gray-50">
          <Button
            variant="outline"
            className="w-full flex items-center justify-center gap-2"
            onClick={() => setShowTutorial(true)}
          >
            <HelpCircle className="w-4 h-4" />
            Ver Tutorial do Sistema Acácia
          </Button>
        </Card>
      </div>
      
      {/* Gamification Modals/Notifications */}
      {showCelebration && (
        <GamificationCelebration
          stage={celebrationStage}
          onClose={() => setShowCelebration(false)}
        />
      )}
      
      {showTutorial && (
        <GamificationTutorial
          onComplete={() => {
            setShowTutorial(false);
            localStorage.setItem('simby_tutorial_seen', 'true');
          }}
          onSkip={() => {
            setShowTutorial(false);
            localStorage.setItem('simby_tutorial_seen', 'true');
          }}
        />
      )}
      
      <PlantHealthNotification
        healthState={user?.health_state}
        onAction={() => {
          // Navigate to appropriate action based on health state
          if (user?.health_state === 'thirsty') {
            // Could open water modal or navigate to profile
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            navigate('/dashboard');
          }
        }}
        onDismiss={() => {
          // Store dismissal in localStorage to not show again for a while
          localStorage.setItem('simby_health_notif_dismissed', Date.now().toString());
        }}
      />
    </div>
  );
};

function PotCard({ pot }) {
  const islandName = ISLAND_NAMES[pot.island_id] || `Ilha ${pot.island_id}`;
  const stage = pot.current_stage ?? 0;
  const pts = pot.island_points ?? 0;
  const isComplete = !!pot.completed_at;

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className={`relative rounded-2xl p-3 ${isComplete ? "bg-yellow-50 border-2 border-yellow-300" : "bg-purple-50"}`}>
        <AcaciaTree
          stage={stage}
          islandId={pot.island_id}
          healthState={pot.health_state || "healthy"}
          size={80}
        />
        {isComplete && (
          <span className="absolute -top-1 -right-1 text-base">🌸</span>
        )}
      </div>
      <span className="text-xs font-semibold text-gray-700">{islandName}</span>
      <span className="text-xs text-gray-400">E{stage} · {pts}pts</span>
    </div>
  );
}

function StatRow({ label, value, sub }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
      <div>
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {sub && <p className="text-xs text-gray-400">{sub}</p>}
      </div>
      <span className="text-xl font-bold text-purple-700">{value}</span>
    </div>
  );
}

export default Profile;
